import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Download, FileText, Clock, CheckCircle2, XCircle, Eye, ChevronDown, Search, Filter, Paperclip, MessageSquare, X, ZoomIn, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { descargoService } from '@/lib/api';

const STATUS_MAP = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  reviewing: { label: 'En revision', color: 'bg-blue-100 text-blue-700', icon: Eye },
  approved: { label: 'Aprobado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const DescargosPanel = () => {
  const [descargos, setDescargos] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_cause: {}, approval_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCause, setFilterCause] = useState('all');
  const [selectedDescargo, setSelectedDescargo] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCause !== 'all') params.cause = filterCause;
      const [listRes, statsRes] = await Promise.all([
        descargoService.list(params),
        descargoService.getStats()
      ]);
      setDescargos(listRes.data.descargos || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar descargos');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCause]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStatusUpdate = async () => {
    if (!selectedDescargo || !newStatus) return;
    try {
      await descargoService.updateStatus(selectedDescargo.id, {
        status: newStatus,
        admin_response: adminResponse || null
      });
      toast.success('Descargo actualizado');
      setSelectedDescargo(null);
      setAdminResponse('');
      setNewStatus('');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleExport = async (format) => {
    try {
      const res = format === 'csv' ? await descargoService.exportCsv() : await descargoService.exportTxt();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `descargos.${format === 'csv' ? 'csv' : 'txt'}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Descargos exportados como ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleDownloadAttachment = async (descargo, index) => {
    try {
      const params = index !== undefined ? `?index=${index}` : '';
      const res = await descargoService.downloadAttachment(descargo.id, params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fallbackName = index !== undefined
        ? (descargo.attachments?.[index]?.filename || `adjunto_${index}`)
        : (descargo.attachment_filename || 'adjuntos.zip');
      link.download = match ? match[1] : fallbackName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar adjunto');
    }
  };

  const isImageFile = (att) => {
    const ct = (att.content_type || '').toLowerCase();
    if (ct.startsWith('image/')) return true;
    const ext = (att.filename || '').toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(ext);
  };

  const loadPreviews = useCallback(async (descargo) => {
    const atts = descargo?.attachments || [];
    const imageAtts = atts.map((att, i) => ({ att, i })).filter(({ att }) => isImageFile(att));
    if (!imageAtts.length) return;
    const newUrls = {};
    await Promise.all(imageAtts.map(async ({ att, i }) => {
      try {
        const res = await descargoService.downloadAttachment(descargo.id, `?index=${i}`);
        newUrls[i] = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'image/jpeg' }));
      } catch { /* skip */ }
    }));
    setPreviewUrls(prev => ({ ...prev, [descargo.id]: newUrls }));
  }, []);

  const openDetail = useCallback((d) => {
    setSelectedDescargo(d);
    setNewStatus(d.status);
    setAdminResponse(d.admin_response || '');
    loadPreviews(d);
  }, [loadPreviews]);

  const causes = [...new Set(descargos.map(d => d.cause))];

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="descargos-panel">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          <p className="text-xs text-slate-500">Total descargos</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{stats.by_status?.pending || 0}</p>
          <p className="text-xs text-slate-500">Pendientes</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.by_status?.approved || 0}</p>
          <p className="text-xs text-slate-500">Aprobados</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.approval_rate}%</p>
          <p className="text-xs text-slate-500">Tasa aprobacion</p>
        </Card>
      </div>

      {/* Stats by cause */}
      {Object.keys(stats.by_cause || {}).length > 0 && (
        <Card className="p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">Por causa</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.by_cause).map(([cause, count]) => (
              <Badge key={cause} variant="outline" className="text-sm py-1 px-3">{cause}: {count}</Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Filters & Export */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-1" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="reviewing">En revision</SelectItem>
            <SelectItem value="approved">Aprobado</SelectItem>
            <SelectItem value="rejected">Rechazado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCause} onValueChange={setFilterCause}>
          <SelectTrigger className="w-[180px]" data-testid="filter-cause">
            <SelectValue placeholder="Causa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las causas</SelectItem>
            {causes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => handleExport('csv')} data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => handleExport('txt')} data-testid="export-txt-btn">
          <FileText className="w-4 h-4 mr-1" /> TXT
        </Button>
      </div>

      {/* Table */}
      {descargos.length === 0 ? (
        <Card className="p-12 text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay descargos {filterStatus !== 'all' || filterCause !== 'all' ? 'con estos filtros' : 'aun'}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left p-3 font-semibold text-slate-600">Fecha</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Cliente</th>
                  <th className="text-left p-3 font-semibold text-slate-600">DNI</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Causa</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Actas</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Estado</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Adjunto</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {descargos.map((d) => {
                  const st = STATUS_MAP[d.status] || STATUS_MAP.pending;
                  return (
                    <tr key={d.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`descargo-row-${d.id}`}>
                      <td className="p-3 text-slate-600">{formatDate(d.created_at)}</td>
                      <td className="p-3 font-medium text-slate-900">{d.client_name || '-'}</td>
                      <td className="p-3 text-slate-600">{d.client_dni || '-'}</td>
                      <td className="p-3"><Badge variant="outline">{d.cause}</Badge></td>
                      <td className="p-3 max-w-[200px]">
                        {d.actas && d.actas.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {d.actas.map((a) => <span key={a} className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded whitespace-nowrap">{a}</span>)}
                          </div>
                        ) : <span className="text-slate-400 text-xs">-</span>}
                      </td>
                      <td className="p-3"><Badge className={`${st.color} font-medium`}>{st.label}</Badge></td>
                      <td className="p-3">
                        {(() => {
                          const atts = d.attachments || [];
                          const hasOldAttachment = !atts.length && d.attachment_filename;
                          if (hasOldAttachment) {
                            return (
                              <Button variant="ghost" size="sm" className="text-blue-600 h-7 px-2" onClick={() => handleDownloadAttachment(d)} data-testid={`download-attachment-${d.id}`}>
                                <Paperclip className="w-3.5 h-3.5 mr-1" />{d.attachment_filename.length > 15 ? d.attachment_filename.substring(0, 15) + '...' : d.attachment_filename}
                              </Button>
                            );
                          }
                          if (atts.length === 0) return <span className="text-slate-400">-</span>;
                          if (atts.length === 1) {
                            return (
                              <Button variant="ghost" size="sm" className="text-blue-600 h-7 px-2" onClick={() => handleDownloadAttachment(d, 0)} data-testid={`download-attachment-${d.id}`}>
                                <Paperclip className="w-3.5 h-3.5 mr-1" />{atts[0].filename.length > 15 ? atts[0].filename.substring(0, 15) + '...' : atts[0].filename}
                              </Button>
                            );
                          }
                          return (
                            <Button variant="ghost" size="sm" className="text-blue-600 h-7 px-2" onClick={() => handleDownloadAttachment(d)} data-testid={`download-attachment-${d.id}`}>
                              <Paperclip className="w-3.5 h-3.5 mr-1" />{atts.length} archivos
                            </Button>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        <Button variant="outline" size="sm" className="h-7" onClick={() => openDetail(d)} data-testid={`manage-descargo-${d.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail/Update Dialog */}
      <Dialog open={!!selectedDescargo} onOpenChange={(open) => { if (!open) { setSelectedDescargo(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" /> Detalle del Descargo
            </DialogTitle>
          </DialogHeader>
          {selectedDescargo && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500">Cliente</p>
                  <p className="font-medium">{selectedDescargo.client_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">DNI</p>
                  <p className="font-medium">{selectedDescargo.client_dni || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Fecha</p>
                  <p className="font-medium">{formatDate(selectedDescargo.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Causa</p>
                  <p className="font-medium">{selectedDescargo.cause}</p>
                </div>
                {selectedDescargo.phone && (
                  <div>
                    <p className="text-slate-500">Telefono</p>
                    <p className="font-medium">{selectedDescargo.phone}</p>
                  </div>
                )}
                {selectedDescargo.email && (
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedDescargo.email}</p>
                  </div>
                )}
              </div>

              {/* Actas */}
              {selectedDescargo.actas && selectedDescargo.actas.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-1.5">Actas del descargo</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedDescargo.actas.map((a, i) => (
                      <span key={a} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 rounded-md font-medium">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedDescargo.observation && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Observacion del cliente</p>
                  <p className="text-sm text-slate-800">{selectedDescargo.observation}</p>
                </div>
              )}

              {/* Attachments with Image Previews */}
              {(() => {
                const atts = selectedDescargo.attachments || [];
                const hasOldAttachment = !atts.length && selectedDescargo.attachment_filename;
                if (hasOldAttachment) {
                  return (
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800">{selectedDescargo.attachment_filename}</span>
                      </div>
                      <Button variant="outline" size="sm" className="h-7" onClick={() => handleDownloadAttachment(selectedDescargo)} data-testid="dialog-download-attachment">
                        <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                      </Button>
                    </div>
                  );
                }
                if (atts.length === 0) return null;
                const currentPreviews = previewUrls[selectedDescargo.id] || {};
                const imageAtts = atts.map((att, i) => ({ att, i })).filter(({ att }) => isImageFile(att));
                const fileAtts = atts.map((att, i) => ({ att, i })).filter(({ att }) => !isImageFile(att));
                return (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500 font-medium">Adjuntos ({atts.length})</p>
                    {/* Image thumbnails grid */}
                    {imageAtts.length > 0 && (
                      <div className="grid grid-cols-3 gap-2" data-testid="attachment-image-grid">
                        {imageAtts.map(({ att, i }) => (
                          <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-100 aspect-square" data-testid={`attachment-preview-${i}`}>
                            {currentPreviews[i] ? (
                              <img src={currentPreviews[i]} alt={att.filename} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-slate-300 animate-pulse" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <button
                                className="p-1.5 bg-white/90 rounded-full hover:bg-white transition-colors"
                                onClick={() => currentPreviews[i] && setLightboxUrl(currentPreviews[i])}
                                data-testid={`preview-enlarge-${i}`}
                              >
                                <ZoomIn className="w-4 h-4 text-slate-700" />
                              </button>
                              <button
                                className="p-1.5 bg-white/90 rounded-full hover:bg-white transition-colors"
                                onClick={() => handleDownloadAttachment(selectedDescargo, i)}
                                data-testid={`preview-download-${i}`}
                              >
                                <Download className="w-4 h-4 text-slate-700" />
                              </button>
                            </div>
                            <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-1.5 py-0.5 truncate">{att.filename}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Non-image files */}
                    {fileAtts.map(({ att, i }) => (
                      <div key={i} className="flex items-center justify-between bg-blue-50 rounded-lg p-2.5" data-testid={`dialog-attachment-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                          <span className="text-sm text-blue-800 truncate">{att.filename}</span>
                        </div>
                        <Button variant="outline" size="sm" className="h-7 shrink-0 ml-2" onClick={() => handleDownloadAttachment(selectedDescargo, i)} data-testid={`dialog-download-attachment-${i}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                    {atts.length > 1 && (
                      <Button variant="outline" size="sm" className="w-full" onClick={() => handleDownloadAttachment(selectedDescargo)} data-testid="dialog-download-all-attachments">
                        <Download className="w-3.5 h-3.5 mr-1" /> Descargar todo (ZIP)
                      </Button>
                    )}
                  </div>
                );
              })()}

              <hr />

              {/* Status Update */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Actualizar estado</p>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="update-status-select">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="reviewing">En revision</SelectItem>
                    <SelectItem value="approved">Aprobado</SelectItem>
                    <SelectItem value="rejected">Rechazado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Respuesta / Nota del admin</p>
                <textarea 
                  className="w-full border rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                  rows={3} 
                  placeholder="Escribe una respuesta o nota..."
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  data-testid="admin-response-input"
                />
              </div>

              <Button className="w-full" onClick={handleStatusUpdate} data-testid="save-descargo-status">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
            onClick={() => setLightboxUrl(null)}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={lightboxUrl}
            alt="Preview"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
        </div>
      )}
    </div>
  );
};

export default DescargosPanel;
